"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = __importStar(require("react"));
var object_assign_1 = __importDefault(require("object-assign"));
var utils_1 = require("./utils");
var hooks_1 = require("./hooks");
var ReactIdSwiperCustom = react_1.forwardRef(function (props, externalRef) {
    var Swiper = props.Swiper, activeSlideKey = props.activeSlideKey, ContainerEl = props.ContainerEl, children = props.children, containerClass = props.containerClass, navigation = props.navigation, noSwiping = props.noSwiping, pagination = props.pagination, parallax = props.parallax, parallaxEl = props.parallaxEl, WrapperEl = props.WrapperEl, wrapperClass = props.wrapperClass, rebuildOnUpdate = props.rebuildOnUpdate, renderScrollbar = props.renderScrollbar, renderPagination = props.renderPagination, renderPrevButton = props.renderPrevButton, renderNextButton = props.renderNextButton, renderParallax = props.renderParallax, rtl = props.rtl, scrollbar = props.scrollbar, shouldSwiperUpdate = props.shouldSwiperUpdate, slideClass = props.slideClass, loop = props.loop, _a = props.modules, modules = _a === void 0 ? [] : _a;
    // Initialize modules to use with swiper
    if (Swiper) {
        Swiper.use(modules);
    }
    // Define swiper instance ref
    var swiperInstanceRef = react_1.useRef(null);
    // Internal ref
    var swiperNodeRef = react_1.useRef(null);
    // Forked ref
    var ref = hooks_1.useForkRef(swiperNodeRef, externalRef);
    // Get current active slide key
    var getActiveSlideIndexFromProps = react_1.useCallback(function () {
        if (!activeSlideKey) {
            return null;
        }
        var activeSlideId = 0;
        // In loop mode first slide index should be 1
        var id = loop ? 1 : 0;
        react_1.Children.forEach(children, function (child) {
            if (react_1.isValidElement(child)) {
                if (child.key === activeSlideKey) {
                    activeSlideId = id;
                }
                id += 1;
            }
        });
        return activeSlideId;
    }, [activeSlideKey, children, loop]);
    // Destroy swiper
    var destroySwiper = react_1.useCallback(function () {
        if (swiperInstanceRef.current !== null) {
            swiperInstanceRef.current.destroy(true, true);
            utils_1.setRef(swiperInstanceRef, null);
        }
    }, []);
    // Initialize swiper
    var buildSwiper = react_1.useCallback(function () {
        if (swiperNodeRef.current && swiperInstanceRef.current === null) {
            utils_1.setRef(swiperInstanceRef, new Swiper(swiperNodeRef.current, object_assign_1.default({}, props)));
        }
    }, [props]);
    // Render slides
    var renderContent = function (e) {
        if (!utils_1.isReactElement(e)) {
            return null;
        }
        var slideClassNames = [slideClass, e.props.className];
        if (noSwiping) {
            slideClassNames.push('swiper-no-swiping');
        }
        return react_1.cloneElement(e, __assign(__assign({}, e.props), { className: slideClassNames.join(' ').trim() }));
    };
    // Destroy Swiper instance when component is unmounted
    react_1.useEffect(function () {
        return function () { return destroySwiper(); };
    }, [destroySwiper]);
    react_1.useEffect(function () {
        buildSwiper();
        if (swiperInstanceRef.current !== null) {
            if (rebuildOnUpdate) {
                destroySwiper();
                buildSwiper();
            }
            else if (shouldSwiperUpdate) {
                swiperInstanceRef.current.update();
            }
            var numSlides = swiperInstanceRef.current.slides.length;
            if (numSlides <= swiperInstanceRef.current.activeIndex) {
                var index = Math.max(numSlides - 1, 0);
                swiperInstanceRef.current.slideTo(index);
            }
            var slideToIndex = getActiveSlideIndexFromProps();
            if (slideToIndex !== null) {
                swiperInstanceRef.current.slideTo(slideToIndex);
            }
        }
    }, [
        destroySwiper,
        getActiveSlideIndexFromProps,
        rebuildOnUpdate,
        shouldSwiperUpdate,
        buildSwiper
    ]);
    // Check modules are loaded before rendering contents
    var shouldRenderParallax = utils_1.isModuleAvailable(modules, 'parallax') && parallax && parallaxEl;
    var shouldRenderPagination = utils_1.isModuleAvailable(modules, 'pagination') && pagination && pagination.el;
    var shouldRenderScrollbar = utils_1.isModuleAvailable(modules, 'scrollbar') && scrollbar && scrollbar.el;
    var isNavigationModuleAvailable = utils_1.isModuleAvailable(modules, 'navigation');
    var shouldRenderNextButton = isNavigationModuleAvailable && navigation && navigation.nextEl;
    var shouldRenderPrevButton = isNavigationModuleAvailable && navigation && navigation.prevEl;
    // No render if wrapper elements are not provided or when modules is empty
    if (!Swiper || !children || !ContainerEl || !WrapperEl) {
        return null;
    }
    // Validate children props
    if (!utils_1.validateChildren(children)) {
        console.warn('Children should be react element or an array of react element!!');
        return null;
    }
    return (react_1.default.createElement(ContainerEl, { className: containerClass, dir: rtl && 'rtl', ref: ref },
        shouldRenderParallax && renderParallax && renderParallax(props),
        react_1.default.createElement(WrapperEl, { className: wrapperClass }, react_1.Children.map(children, renderContent)),
        shouldRenderPagination && renderPagination && renderPagination(props),
        shouldRenderScrollbar && renderScrollbar && renderScrollbar(props),
        shouldRenderNextButton && renderNextButton && renderNextButton(props),
        shouldRenderPrevButton && renderPrevButton && renderPrevButton(props)));
});
// Default props
ReactIdSwiperCustom.defaultProps = {
    containerClass: 'swiper-container',
    wrapperClass: 'swiper-wrapper',
    slideClass: 'swiper-slide',
    ContainerEl: 'div',
    WrapperEl: 'div',
    renderScrollbar: function (_a) {
        var scrollbar = _a.scrollbar;
        return scrollbar ? react_1.default.createElement("div", { className: utils_1.classNames(scrollbar.el) }) : null;
    },
    renderPagination: function (_a) {
        var pagination = _a.pagination;
        return pagination ? react_1.default.createElement("div", { className: utils_1.classNames(pagination.el) }) : null;
    },
    renderPrevButton: function (_a) {
        var navigation = _a.navigation;
        return navigation ? react_1.default.createElement("div", { className: utils_1.classNames(navigation.prevEl) }) : null;
    },
    renderNextButton: function (_a) {
        var navigation = _a.navigation;
        return navigation ? react_1.default.createElement("div", { className: utils_1.classNames(navigation.nextEl) }) : null;
    },
    renderParallax: function (_a) {
        var parallaxEl = _a.parallaxEl;
        return parallaxEl ? (react_1.default.createElement("div", { className: utils_1.classNames(parallaxEl.el), "data-swiper-parallax": parallaxEl.value })) : null;
    },
    modules: []
};
ReactIdSwiperCustom.displayName = 'ReactIdSwiper';
exports.default = ReactIdSwiperCustom;
